import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import React from 'react';
import { RenderedContent } from '../utils';
import { cn } from '@workspace/utils';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: RenderedContent;
  subTitle?: RenderedContent;
  className?: string;
  closeButton?: boolean;
}

export function Modal({ open, onClose, children, title, subTitle, className, closeButton }: ModalProps) {
  const cancelButtonRef = React.useRef(null);

  return (
    <Dialog
      initialFocus={cancelButtonRef}
      onClose={onClose}
      open={open}
      transition
      className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4 transition duration-300 ease-out data-[closed]:opacity-0"
    >
      <DialogPanel
        className={cn('relative transform flex flex-col rounded-t-lg sm:rounded-lg bg-white text-left shadow-xl transition-all sm:m-8 w-full', className)}
      >
        <Modal.Header
          closeModal={onClose}
          title={title}
          subTitle={subTitle}
          closeButton={closeButton}
        />
        {children}
      </DialogPanel>
    </Dialog>
  );
}

export interface ModalHeaderProps {
  title?: RenderedContent;
  subTitle?: RenderedContent;
  closeModal: () => void;
  closeButton?: boolean;
}

Modal.Header = function ({ title, subTitle, closeModal, closeButton }: ModalHeaderProps) {
  return (
    <div className="px-4 sm:px-8 pt-5 flex items-start justify-between gap-2">
      <div>
        {title ? (
          <DialogTitle
            as="h1"
            className="text-base sm:text-xl font-medium"
          >
            {title}
          </DialogTitle>
        ) : null}
        {subTitle ? <Description className="text-sm text-secondary">{subTitle}</Description> : null}
      </div>

      {closeButton ? (
        <button
          className="p-1 hover:bg-gray-50 rounded-md"
          onClick={closeModal}
          type="button"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 18 18 6M6 6l12 12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
};

Modal.Body = function ({ children, className, ...props }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('px-4 sm:px-8', className)}
      {...props}
    >
      {children}
    </div>
  );
};
